import React, { Component } from 'react';
import compose from 'lodash/flowRight';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Badge from '@material-ui/core/Badge';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { Menu as MenuIcon, ChatBubbleSharp, ExitToApp, Person, Settings } from '@material-ui/icons';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';

import { ReactComponent as Logo } from '../assets/svgs/logo.svg';
import { ReactComponent as HelpIcon } from '../assets/svgs/icon-need-help.svg';
import { store } from '../config/Store';
import { colors } from '../themes/colors';
import { NavOptionsMenu } from './NavOptionsMenu';
import HelpLink from './HelpLink/HelpLink';

const styles = (theme) => ({
  grow: {
    flexGrow: 1,
  },
  divider: {
    borderRight: '1px solid white',
    marginRight: 10,
    marginLeft: 18, // 10 + 8 = 18. 10 to match marginRght. 8 to match padding on logout button.
    height: 30,
  },
  sectionDesktop: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  sectionMobile: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
    },
  },
  mobileMenuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    color: colors.gray2,
  },
  dot: {
    ...theme.bigDot,
  },
  boldValue: {
    color: colors.white1,
    fontWeight: 800,
  },
  orangeBadge: {
    backgroundColor: colors.orange1,
  },
  redBadge: {
    backgroundColor: colors.red3,
  },
});

const MobileMenu = ({
  mobileMoreAnchorEl,
  currentUser,
  showChatBadge,
  isCoachProvider,
  classes,
  handleMobileMenuClose,
  logout,
}) => {
  const rosterCountRunningLow = [1, 2].includes(currentUser.newClientCapacity) && isCoachProvider;
  const rosterCountIsZero = currentUser.newClientCapacity === 0 && isCoachProvider;

  return (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={!!mobileMoreAnchorEl}
      onClose={handleMobileMenuClose}
    >
      <MenuItem onClick={handleMobileMenuClose} className={classes.mobileMenuItem}>
        <Typography variant="body1" color="inherit">
          {currentUser.firstName} {currentUser.lastName}
        </Typography>
      </MenuItem>
      <MenuItem onClick={logout} className={classes.mobileMenuItem}>
        <Typography variant="body1" color="inherit">
          Log out
        </Typography>
        <ExitToApp />
      </MenuItem>
      <MenuItem
        component={Link}
        className={classes.mobileMenuItem}
        onClick={handleMobileMenuClose}
        to="/clients"
      >
        <Typography variant="body1" color="inherit">
          Clients
        </Typography>
        <Person />
      </MenuItem>
      <MenuItem
        component={Link}
        className={classes.mobileMenuItem}
        onClick={handleMobileMenuClose}
        to="/chat"
      >
        <Typography variant="body1" color="inherit">
          Chat
        </Typography>
        <Badge color="secondary" variant="dot" invisible={!showChatBadge}>
          <ChatBubbleSharp />
        </Badge>
      </MenuItem>
      <MenuItem
        component={Link}
        className={classes.mobileMenuItem}
        onClick={handleMobileMenuClose}
        to="/settings"
      >
        <Typography variant="body1" color="inherit">
          Settings
        </Typography>
        <Badge
          variant="dot"
          invisible={!rosterCountRunningLow && !rosterCountIsZero}
          classes={{ badge: rosterCountIsZero ? classes.redBadge : classes.orangeBadge }}
        >
          <Settings />
        </Badge>
      </MenuItem>
    </Menu>
  );
};

const LogoutButton = ({ onClick }) => (
  <Button color="inherit" onClick={onClick}>
    <Typography variant="body1" color="inherit">
      Log out
    </Typography>
  </Button>
);

const LoginButton = () => (
  <Button color="inherit" component={Link} to="/login">
    <Typography variant="body1" color="inherit">
      Login
    </Typography>
  </Button>
);

class NavBar extends Component {
  state = {
    mobileMoreAnchorEl: null,
  };

  logout = async () => {
    this.setState({ mobileMoreAnchorEl: null });
    this.props.logout({ nextUrl: '/' });
  };

  handleMobileMenuOpen = (event) => {
    this.setState({ mobileMoreAnchorEl: event.currentTarget });
  };

  handleMobileMenuClose = () => {
    this.setState({ mobileMoreAnchorEl: null });
  };

  render() {
    const { classes, isAuthenticated, currentUser, unreadMessagesCount } = this.props;
    const { mobileMoreAnchorEl } = this.state;

    return (
      <div className={classes.root}>
        <AppBar position="static" color="primary">
          <Toolbar>
            <Link to="/">
              <Logo />
            </Link>
            <div className={classes.grow} />
            <div className={classes.sectionDesktop}>
              {isAuthenticated ? (
                <React.Fragment>
                  <HelpLink className="flex gap-3 items-center px-2 justify-between font-semibold focus:outline-none cursor-pointer">
                    <span className="mb-1">
                      <HelpIcon />
                    </span>
                    Need help
                  </HelpLink>
                  <div className={classes.divider} />
                  {/* TODO: Design changes for CI-2294 required us to remove the PresenceStatusPicker
                    for now. No current designs for integrating the status picker into the nav options
                    dropdown behavior. */}
                  <NavOptionsMenu
                    logoutFn={this.logout}
                    openerText={`${currentUser.firstName} ${currentUser.lastName}`}
                  />
                </React.Fragment>
              ) : (
                <LoginButton />
              )}
            </div>
            <div className={classes.sectionMobile}>
              {isAuthenticated ? (
                currentUser.providerInvoicingOnly ? (
                  <LogoutButton onClick={this.logout} />
                ) : (
                  <>
                    <IconButton
                      aria-haspopup="true"
                      onClick={this.handleMobileMenuOpen}
                      color="inherit"
                    >
                      <Badge
                        color="secondary"
                        variant="dot"
                        invisible={!unreadMessagesCount}
                        classes={{ dot: classes.dot }}
                      >
                        <MenuIcon />
                      </Badge>
                    </IconButton>
                  </>
                )
              ) : (
                <LoginButton />
              )}
            </div>
          </Toolbar>
        </AppBar>
        <MobileMenu
          mobileMoreAnchorEl={mobileMoreAnchorEl}
          currentUser={currentUser}
          showChatBadge={unreadMessagesCount}
          showRosterCountLowBadge={currentUser.isCoach}
          classes={classes}
          handleMobileMenuClose={this.handleMobileMenuClose}
          logout={this.logout}
        />
      </div>
    );
  }
}

const mapState = (state) => ({
  isAuthenticated: state.user.isAuthenticated,
  currentUser: state.user.currentUser,
  unreadMessagesCount: store.select.chat.unreadMessagesCount(state),
  isCoachProvider: state.user.currentUser.isCoach,
});

const mapDispatch = (store) => ({
  setIsAuthenticated: (isAuthenticated) => store.user.setIsAuthenticated(isAuthenticated),
  logout: (data) => store.user.logout(data),
});

export default compose(withStyles(styles), connect(mapState, mapDispatch))(NavBar);
